import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, Box, Text, Card, Flex } from '@infotrack/zenith-ui';

import { useITopFiveApiV2EndpointsAuthMeEndpoint } from '@/api/api.generated';

type PermissionKey = 'hasAddTopFiveAccess' | 'hasiTopFiveCompanyAccess' | 'hasCalculateTopFiveBonusesAccess' | 'hasTopFiveResultEditLockedAccess' | 'hasiNominateAccess' | 'hasiTopFiveStaffEditorAccess';

interface RequirePermissionProps {
  children: React.ReactNode;
  permission: PermissionKey | PermissionKey[];
  fallbackPath?: string;
}

/**
 * Route guard that checks if the user has a specific permission or any of the specified permissions.
 * If the user doesn't have the required permission(s), shows an access denied message.
 * When an array of permissions is provided, uses OR logic (user needs at least one).
 */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  children,
  permission,
  fallbackPath = '/services/itopfiveui/topfive',
}) => {
  const location = useLocation();

  const {
    data: authResponse,
    isLoading: isAuthLoading,
    error: authError,
  } = useITopFiveApiV2EndpointsAuthMeEndpoint({
    query: {
      retry: (failureCount, error) => {
        const isUnauthorizedError = (err: unknown): boolean => {
          if (!err || typeof err !== 'object') return false;
          const errorObj = err as {
            status?: number;
            response?: { status?: number };
          };
          return errorObj.status === 401 || errorObj.response?.status === 401;
        };

        const isAuthError = isUnauthorizedError(error);
        return !isAuthError && failureCount < 2;
      },
      retryDelay: 1000,
    },
  });

  // Show loading state while checking authentication
  if (isAuthLoading) {
    return <Spinner fullSize />;
  }

  // Redirect to login if not authenticated or auth error
  if (authError || !authResponse?.data) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has the required permission(s)
  const permissions = Array.isArray(permission) ? permission : [permission];
  const hasPermission = permissions.some(
    (perm) => authResponse.data.permissions?.[perm] ?? false
  );

  if (!hasPermission) {
    return (
      <Flex
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: 4,
          backgroundColor: 'neutral/50',
        }}
      >
        <Box sx={{ maxWidth: '500px', width: '100%' }}>
          <Card
            padding="large"
            borderRadius="medium"
            borderColor="neutral/200"
            boxShadow="medium"
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Text variant="title" as="h1" color="error">
                Access Denied
              </Text>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Text variant="medium">
                You do not have permission to access this page.
              </Text>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Text variant="small" color="neutral/600">
                If you believe this is an error, please contact your administrator.
              </Text>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <a
                href={fallbackPath}
                style={{
                  color: '#0066cc',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Go to Home
              </a>
            </Box>
          </Card>
        </Box>
      </Flex>
    );
  }

  return <>{children}</>;
};
